import { useSessionStore } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { ClinicView } from "@rooms/ClinicView";
import { useRoomsList } from "@rooms/roomsQueries";
import { PanelError } from "@scribenote/brian-design-system";
import { SubjectPane } from "@subjects/SubjectPane";
import { useSubject } from "@subjects/subjectsQueries";
import { ViewWorkspacePane } from "@views/ViewWorkspacePane";
import { findBoardVisit } from "@visits/visitsClient";
import { useOpenVisits, useTodayVisits } from "@visits/visitsQueries";
import {
  useWorkspaceNavigation,
  useWorkspaceSelection,
} from "@workspace/useWorkspaceNavigation";

/**
 * Main content router for the workspace, rendering by `resolveSelection`
 * precedence; with nothing selected it shows the Clinic View (rooms grid).
 *
 * A selected room renders as a subject in the same `SubjectPane` as everyone else,
 * its occupants listed in the pane's rail. Clicking one selects their visit, which
 * then wins by precedence.
 *
 * The on-screen subject is not synced into a store from here; `useActiveSubject`
 * derives it from the same URL selection.
 *
 * Padding and scrolling belong to the pane, not the window (WEB-3288): the subject
 * and view panes run full bleed and scroll region by region, so only the Clinic
 * View gets a padded scroll container here.
 *
 * The window itself is the corkboard (CompanionShell owns that surface), so every
 * container here stays transparent: room cards pin straight to the board and the
 * subject folder lies on it.
 */
export const WorkspaceContent = () => (
  <AsyncBoundary
    skeleton={null}
    fallback={(retry) => <PanelError label="rooms" onRetry={retry} />}
  >
    <WorkspaceContentInner />
  </AsyncBoundary>
);

const WorkspaceContentInner = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: rooms } = useRoomsList(sessionKey, true);
  const { data: visits } = useTodayVisits(sessionKey);
  const { data: openVisits, isPending: openPending } =
    useOpenVisits(sessionKey);
  const { selection } = useWorkspaceSelection();
  const { setViewedView, closeSubject } = useWorkspaceNavigation();

  // Closing any pane below exits to the rooms grid outright - `closeSubject`
  // clears room, visit, subject, and view together, so it's the single "exit"
  // for every pane here (a subject opened from search may have no relation to
  // the room/visit that preceded it, so exiting lands somewhere neutral).
  const exitToRooms = closeSubject;

  // Rules of hooks: call this on every render regardless of mode. `useSubject`
  // gates its own fetch on a non-null id, so this costs nothing outside
  // subject mode.
  const subjectQuery = useSubject(
    selection.mode === "subject" ? selection.subjectId : null,
  );

  // A SavedView opened from search wins over everything underneath it.
  if (selection.mode === "view")
    return (
      <ViewWorkspacePane
        id={selection.viewId}
        onClear={() => setViewedView(null)}
      />
    );

  // A subject opened from search wins over everything except a view. While
  // the fetch is pending, render nothing rather than flashing the rooms grid.
  // `openSubject` clears room/visit, so there's nothing underneath a subject:
  // a fetch that settles with no record means a stale param (deleted record,
  // or a foreign uuid on a deep link), and the checks below won't match in
  // subject mode - so it falls through to the clinic grid, which matches
  // `useActiveSubject` resolving to null.
  if (selection.mode === "subject") {
    if (subjectQuery.isPending) return null;
    if (subjectQuery.data)
      return <SubjectPane subject={subjectQuery.data} onClear={exitToRooms} />;
  }

  // A selected visit wins over the room it's in, whether it's on today's board or
  // still open from an earlier day (WEB-3271). An id in neither list falls
  // through to the room logic, using the room the visit carried.
  const selectedVisit = findBoardVisit(
    selection.mode === "visit" ? selection.visitUuid : null,
    visits,
    openVisits,
  );
  // Today's list suspends, the still-open one doesn't, so on a cold deep link
  // wait for it rather than flashing the grid on the way to the pane.
  if (selection.mode === "visit" && !selectedVisit && openPending) return null;
  if (selectedVisit)
    return (
      <SubjectPane
        subject={{
          id: selectedVisit.patientId,
          name: selectedVisit.patientName,
          kind: "patient",
        }}
        visit={selectedVisit}
        onClear={exitToRooms}
      />
    );

  const roomUuid =
    selection.mode === "visit" || selection.mode === "room"
      ? selection.roomUuid
      : null;
  const selectedRoom = roomUuid
    ? (rooms.find((room) => room.id === roomUuid) ?? null)
    : null;

  // A selected room renders as a subject like everyone else; its occupants show in
  // the pane's rail. Clearing exits to the Clinic View.
  if (selectedRoom)
    return (
      <SubjectPane
        subject={{
          id: selectedRoom.id,
          name: selectedRoom.name,
          kind: "room",
        }}
        onClear={exitToRooms}
      />
    );

  // The one padded scroll container in this file, and it stays transparent: the
  // cork underneath is the Clinic View's surface.
  return (
    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-7">
      <ClinicView />
    </div>
  );
};
